import { z } from 'zod';
import { financePaymentMethodSchema, financePaymentRefundSchema, financePaymentSchema, } from './finance-invoice-lifecycle.contract.js';
import { financeDateOnlySchema, financeInvoiceCommandIdSchema, financeMoneyCentsSchema, } from './finance-invoice.contract.js';
export const financePaymentReconciliationCapability = 'finance.payment-reconciliation.v1';
export const financePaymentReconciliationIdSchema = z.string().uuid();
export const financePaymentProviderKeySchema = z.string()
    .regex(/^[a-z][a-z0-9-]{0,49}$/);
export const financePaymentExternalReferenceSchema = z.string().trim().min(1).max(240);
export const financePaymentSignedCentsSchema = z.number().int()
    .min(-1_000_000_000_000_000)
    .max(1_000_000_000_000_000);
export const financePaymentNonzeroSignedCentsSchema = financePaymentSignedCentsSchema.refine((value) => value !== 0, 'A signed Finance amount cannot be zero.');
export const financePaymentClearingStatusSchema = z.enum([
    'pending', 'cleared', 'failed', 'refunded', 'disputed', 'reversed',
]);
export const financePaymentProcessorEntryKindSchema = z.enum([
    'charge', 'refund', 'adjustment',
]);
export const financePaymentPayoutStatusSchema = z.enum([
    'pending', 'paid', 'failed', 'canceled',
]);
export const financePaymentPayoutAllocationStatusSchema = z.enum(['active', 'reversed']);
export const financePaymentDisputeStatusSchema = z.enum([
    'open', 'under-review', 'won', 'lost', 'withdrawn',
]);
const nullableShortText = z.string().trim().min(1).max(240).nullable();
const nullableLongText = z.string().trim().min(1).max(2_000).nullable();
const timestampSchema = z.string().datetime({ offset: true });
const positiveRevisionSchema = z.number().int().positive();
export const financePaymentProcessorEntrySchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    tenantId: financePaymentReconciliationIdSchema,
    id: financePaymentReconciliationIdSchema,
    paymentId: financePaymentReconciliationIdSchema,
    sourceTransactionId: financePaymentReconciliationIdSchema.nullable(),
    providerKey: financePaymentProviderKeySchema,
    externalTransactionReference: financePaymentExternalReferenceSchema,
    kind: financePaymentProcessorEntryKindSchema,
    occurredAt: timestampSchema,
    grossCents: financePaymentNonzeroSignedCentsSchema,
    feeCents: financePaymentSignedCentsSchema,
    netCents: financePaymentNonzeroSignedCentsSchema,
    currencyCode: z.literal('USD'),
    paymentIntentReference: nullableShortText,
    chargeReference: nullableShortText,
    balanceTransactionReference: nullableShortText,
    payoutReference: nullableShortText,
    processorStatus: nullableShortText,
    reason: z.string().trim().min(1).max(2_000),
    recordedByMembershipId: financePaymentReconciliationIdSchema,
    createdAt: timestampSchema,
}).superRefine((value, context) => {
    if (value.grossCents + value.feeCents !== value.netCents) {
        context.addIssue({
            code: 'custom', path: ['netCents'],
            message: 'Processor gross plus signed fee must equal exact net cents.',
        });
    }
    if (value.kind === 'charge' && value.grossCents < 1) {
        context.addIssue({
            code: 'custom', path: ['grossCents'], message: 'A charge gross amount must be positive.',
        });
    }
    if (value.kind === 'refund' && value.grossCents > -1) {
        context.addIssue({
            code: 'custom', path: ['grossCents'], message: 'A refund gross amount must be negative.',
        });
    }
});
export const financePaymentPayoutSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    tenantId: financePaymentReconciliationIdSchema,
    id: financePaymentReconciliationIdSchema,
    revision: positiveRevisionSchema,
    providerKey: financePaymentProviderKeySchema,
    externalPayoutReference: financePaymentExternalReferenceSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    arrivalDate: financeDateOnlySchema.nullable(),
    destinationReference: nullableShortText,
    balanceTransactionReference: nullableShortText,
    status: financePaymentPayoutStatusSchema,
    statusOccurredAt: timestampSchema,
    failureCode: nullableShortText,
    failureMessage: nullableLongText,
    reason: z.string().trim().min(1).max(2_000),
    recordedByMembershipId: financePaymentReconciliationIdSchema,
    lastChangedByMembershipId: financePaymentReconciliationIdSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
}).superRefine((value, context) => {
    if (value.status !== 'failed' &&
        (value.failureCode !== null || value.failureMessage !== null)) {
        context.addIssue({
            code: 'custom', path: ['failureCode'],
            message: 'Only a failed payout may carry failure evidence.',
        });
    }
    if (value.status === 'failed' && value.failureCode === null && value.failureMessage === null) {
        context.addIssue({
            code: 'custom', path: ['failureCode'],
            message: 'A failed payout requires a failure code or message.',
        });
    }
});
export const financePaymentPayoutAllocationSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    tenantId: financePaymentReconciliationIdSchema,
    id: financePaymentReconciliationIdSchema,
    revision: positiveRevisionSchema,
    payoutId: financePaymentReconciliationIdSchema,
    processorEntryId: financePaymentReconciliationIdSchema,
    paymentId: financePaymentReconciliationIdSchema,
    allocatedNetCents: financeMoneyCentsSchema.min(1),
    status: financePaymentPayoutAllocationStatusSchema,
    reason: z.string().trim().min(1).max(2_000),
    recordedByMembershipId: financePaymentReconciliationIdSchema,
    reversedByMembershipId: financePaymentReconciliationIdSchema.nullable(),
    reversedAt: timestampSchema.nullable(),
    reversalReason: nullableLongText,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
}).superRefine((value, context) => {
    const reversedEvidence = value.reversedByMembershipId !== null &&
        value.reversedAt !== null && value.reversalReason !== null;
    if ((value.status === 'reversed') !== reversedEvidence) {
        context.addIssue({
            code: 'custom', path: ['reversedAt'],
            message: 'Reversed payout allocations require complete retained evidence.',
        });
    }
});
export const financePaymentDisputeSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    tenantId: financePaymentReconciliationIdSchema,
    id: financePaymentReconciliationIdSchema,
    revision: positiveRevisionSchema,
    paymentId: financePaymentReconciliationIdSchema,
    providerKey: financePaymentProviderKeySchema,
    externalDisputeReference: financePaymentExternalReferenceSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    reasonCode: nullableShortText,
    status: financePaymentDisputeStatusSchema,
    processorStatus: nullableShortText,
    evidenceDueAt: timestampSchema.nullable(),
    chargeRefundable: z.boolean().nullable(),
    statusOccurredAt: timestampSchema,
    reason: z.string().trim().min(1).max(2_000),
    recordedByMembershipId: financePaymentReconciliationIdSchema,
    lastChangedByMembershipId: financePaymentReconciliationIdSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export { financePaymentRefundSchema };
export const financePaymentBankClearingSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    tenantId: financePaymentReconciliationIdSchema,
    statementId: financePaymentReconciliationIdSchema,
    transactionId: financePaymentReconciliationIdSchema,
    paymentId: financePaymentReconciliationIdSchema,
    postedDate: financeDateOnlySchema,
    amountCents: financeMoneyCentsSchema.min(1),
    sourceReference: nullableShortText,
    resolvedByMembershipId: financePaymentReconciliationIdSchema,
    resolvedAt: timestampSchema,
});
export const financePaymentReconciliationDetailSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    payment: financePaymentSchema,
    invoiceNumber: z.string().trim().min(1).max(80),
    invoiceRevision: positiveRevisionSchema,
    clearingStatus: financePaymentClearingStatusSchema,
    isCleared: z.boolean(),
    refundedCents: financeMoneyCentsSchema,
    netReceivedCents: financeMoneyCentsSchema,
    processorEntries: z.array(financePaymentProcessorEntrySchema).max(500),
    payouts: z.array(financePaymentPayoutSchema).max(500),
    payoutAllocations: z.array(financePaymentPayoutAllocationSchema).max(500),
    disputes: z.array(financePaymentDisputeSchema).max(500),
    refunds: z.array(financePaymentRefundSchema).max(500),
    bankClearings: z.array(financePaymentBankClearingSchema).max(10),
}).superRefine((value, context) => {
    const recordedRefunds = value.refunds.reduce((sum, refund) => sum + refund.amountCents, 0);
    const reversalRefund = value.payment.status === 'reversed' &&
        value.payment.reversalKind === 'refunded'
        ? value.payment.amountCents
        : 0;
    const expectedRefunded = Math.max(recordedRefunds, reversalRefund);
    const expectedNet = value.payment.status === 'completed'
        ? value.payment.amountCents - recordedRefunds
        : 0;
    if (value.refundedCents !== expectedRefunded) {
        context.addIssue({
            code: 'custom', path: ['refundedCents'],
            message: 'Payment refund totals do not reconcile.',
        });
    }
    if (value.netReceivedCents !== expectedNet) {
        context.addIssue({
            code: 'custom', path: ['netReceivedCents'],
            message: 'Payment net received cents do not reconcile.',
        });
    }
    if (value.isCleared !== (value.clearingStatus === 'cleared')) {
        context.addIssue({
            code: 'custom', path: ['isCleared'],
            message: 'Payment clearing boolean does not match its status.',
        });
    }
});
export const financePaymentReconciliationListQuerySchema = z.strictObject({
    clearingStatus: financePaymentClearingStatusSchema.optional(),
    method: financePaymentMethodSchema.optional(),
    search: z.string().trim().min(1).max(240).optional(),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().min(0).max(1_000_000).default(0),
});
export const financePaymentReconciliationPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
export const financePaymentReconciliationListResponseSchema = z.strictObject({
    data: z.array(financePaymentReconciliationDetailSchema),
    page: financePaymentReconciliationPageSchema,
});
export const financePaymentReconciliationDashboardSchema = z.strictObject({
    contractVersion: z.literal(financePaymentReconciliationCapability),
    paymentCount: z.number().int().nonnegative(),
    clearedCount: z.number().int().nonnegative(),
    pendingCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    refundedCount: z.number().int().nonnegative(),
    disputedCount: z.number().int().nonnegative(),
    reversedCount: z.number().int().nonnegative(),
    grossReceivedCents: financeMoneyCentsSchema,
    refundedCents: financeMoneyCentsSchema,
    netReceivedCents: financeMoneyCentsSchema,
    clearedCents: financeMoneyCentsSchema,
    pendingCents: financeMoneyCentsSchema,
}).superRefine((value, context) => {
    if (value.paymentCount !== value.clearedCount + value.pendingCount + value.failedCount +
        value.refundedCount + value.disputedCount + value.reversedCount) {
        context.addIssue({
            code: 'custom', path: ['paymentCount'],
            message: 'Payment clearing counts do not reconcile.',
        });
    }
    if (value.grossReceivedCents - value.refundedCents !== value.netReceivedCents) {
        context.addIssue({
            code: 'custom', path: ['netReceivedCents'],
            message: 'Payment received totals do not reconcile.',
        });
    }
});
const commandReasonSchema = z.string().trim().min(1).max(2_000);
export const financeRecordPaymentProcessorEntryCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    entryId: financePaymentReconciliationIdSchema,
    paymentId: financePaymentReconciliationIdSchema,
    sourceTransactionId: financePaymentReconciliationIdSchema.nullable().default(null),
    providerKey: financePaymentProviderKeySchema,
    externalTransactionReference: financePaymentExternalReferenceSchema,
    kind: financePaymentProcessorEntryKindSchema,
    occurredAt: timestampSchema,
    grossCents: financePaymentNonzeroSignedCentsSchema,
    feeCents: financePaymentSignedCentsSchema,
    netCents: financePaymentNonzeroSignedCentsSchema,
    currencyCode: z.literal('USD'),
    paymentIntentReference: nullableShortText.default(null),
    chargeReference: nullableShortText.default(null),
    balanceTransactionReference: nullableShortText.default(null),
    payoutReference: nullableShortText.default(null),
    processorStatus: nullableShortText.default(null),
    reason: commandReasonSchema,
}).superRefine((value, context) => {
    if (value.grossCents + value.feeCents !== value.netCents) {
        context.addIssue({
            code: 'custom', path: ['netCents'],
            message: 'Processor gross plus signed fee must equal exact net cents.',
        });
    }
    if (value.kind === 'charge' && value.grossCents < 1) {
        context.addIssue({ code: 'custom', path: ['grossCents'], message: 'Charge gross must be positive.' });
    }
    if (value.kind === 'refund' && value.grossCents > -1) {
        context.addIssue({ code: 'custom', path: ['grossCents'], message: 'Refund gross must be negative.' });
    }
});
export const financeRecordPaymentPayoutCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    payoutId: financePaymentReconciliationIdSchema,
    providerKey: financePaymentProviderKeySchema,
    externalPayoutReference: financePaymentExternalReferenceSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    arrivalDate: financeDateOnlySchema.nullable().default(null),
    destinationReference: nullableShortText.default(null),
    balanceTransactionReference: nullableShortText.default(null),
    status: financePaymentPayoutStatusSchema,
    statusOccurredAt: timestampSchema,
    failureCode: nullableShortText.default(null),
    failureMessage: nullableLongText.default(null),
    reason: commandReasonSchema,
}).superRefine((value, context) => {
    if (value.status !== 'failed' &&
        (value.failureCode !== null || value.failureMessage !== null)) {
        context.addIssue({ code: 'custom', path: ['failureCode'], message: 'Failure evidence is invalid.' });
    }
    if (value.status === 'failed' && value.failureCode === null && value.failureMessage === null) {
        context.addIssue({ code: 'custom', path: ['failureCode'], message: 'Failure evidence is required.' });
    }
});
export const financeUpdatePaymentPayoutStatusCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: positiveRevisionSchema,
    status: financePaymentPayoutStatusSchema,
    statusOccurredAt: timestampSchema,
    failureCode: nullableShortText.default(null),
    failureMessage: nullableLongText.default(null),
    reason: commandReasonSchema,
}).superRefine((value, context) => {
    if (value.status !== 'failed' &&
        (value.failureCode !== null || value.failureMessage !== null)) {
        context.addIssue({ code: 'custom', path: ['failureCode'], message: 'Failure evidence is invalid.' });
    }
    if (value.status === 'failed' && value.failureCode === null && value.failureMessage === null) {
        context.addIssue({ code: 'custom', path: ['failureCode'], message: 'Failure evidence is required.' });
    }
});
export const financeAllocatePaymentPayoutCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    allocationId: financePaymentReconciliationIdSchema,
    processorEntryId: financePaymentReconciliationIdSchema,
    allocatedNetCents: financeMoneyCentsSchema.min(1),
    reason: commandReasonSchema,
});
export const financeReversePaymentPayoutAllocationCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: positiveRevisionSchema,
    reason: commandReasonSchema,
});
export const financeRecordPaymentDisputeCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    disputeId: financePaymentReconciliationIdSchema,
    paymentId: financePaymentReconciliationIdSchema,
    providerKey: financePaymentProviderKeySchema,
    externalDisputeReference: financePaymentExternalReferenceSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    reasonCode: nullableShortText.default(null),
    status: financePaymentDisputeStatusSchema,
    processorStatus: nullableShortText.default(null),
    evidenceDueAt: timestampSchema.nullable().default(null),
    chargeRefundable: z.boolean().nullable().default(null),
    statusOccurredAt: timestampSchema,
    reason: commandReasonSchema,
});
export const financeUpdatePaymentDisputeStatusCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedRevision: positiveRevisionSchema,
    status: financePaymentDisputeStatusSchema,
    processorStatus: nullableShortText.default(null),
    statusOccurredAt: timestampSchema,
    reason: commandReasonSchema,
});
export const financeRecordPaymentRefundCommandSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    refundId: financePaymentReconciliationIdSchema,
    expectedInvoiceRevision: positiveRevisionSchema,
    providerKey: financePaymentProviderKeySchema,
    externalRefundReference: financePaymentExternalReferenceSchema,
    amountCents: financeMoneyCentsSchema.min(1),
    currencyCode: z.literal('USD'),
    refundedOn: financeDateOnlySchema,
    processorStatus: nullableShortText.default(null),
    reason: commandReasonSchema,
});
export const financePaymentReconciliationCommandResponseSchema = z.strictObject({
    data: financePaymentReconciliationDetailSchema,
    command: z.strictObject({
        commandId: financeInvoiceCommandIdSchema,
        idempotent: z.boolean(),
    }),
});
export const financePaymentPayoutCommandResponseSchema = z.strictObject({
    data: financePaymentPayoutSchema,
    command: z.strictObject({
        commandId: financeInvoiceCommandIdSchema,
        idempotent: z.boolean(),
    }),
});
export const financePaymentPayoutListResponseSchema = z.strictObject({
    data: z.array(financePaymentPayoutSchema).max(500),
});
export function deriveFinancePaymentClearing(input) {
    if (input.paymentStatus === 'reversed') {
        if (input.reversalKind === 'failed')
            return 'failed';
        if (input.reversalKind === 'refunded')
            return 'refunded';
        return 'reversed';
    }
    if (input.paymentStatus === 'pending')
        return 'pending';
    if (input.refundedCents >= input.paymentAmountCents)
        return 'refunded';
    if (input.disputeStatuses.some((status) => status === 'open' || status === 'under-review' || status === 'lost')) {
        return 'disputed';
    }
    if (input.disputeStatuses.includes('won'))
        return 'cleared';
    if (input.hasBankClearing || input.hasPaidPayoutAllocation)
        return 'cleared';
    return 'pending';
}
//# sourceMappingURL=finance-payment-reconciliation.contract.js.map